/**
 * Etichette e dettaglio leggibile degli eventi di audit per la pagina Log Audit (E7.6).
 *
 * `KNOWN_ENTITY_TYPES` sono i tipi entità emessi dai servizi; la pagina li unisce a quelli
 * effettivamente presenti nel log (GET /audit/facets), così un tipo nuovo compare nel filtro anche
 * prima che questa lista venga aggiornata.
 */
import type { AuditEventRow } from './audit-api.js';
import { auditChangeRows, formatAuditValue, type AuditChangeRow } from './AuditTrail.js';

export const ENTITY_TYPE_LABEL: Record<string, string> = {
  admin_secret: 'Segreti admin (.env)',
  auth: 'Accesso / RBAC',
  auto_trigger: 'Trigger automatici',
  batch_edit: 'Modifiche massive',
  branding: 'Branding',
  component_bom: 'BOM componente',
  'csv-import': 'Import CSV',
  customer: 'Cliente',
  customer_contact: 'Contatto cliente',
  customer_site: 'Sito cliente',
  customer_vuln_notification: 'Notifica vulnerabilità cliente',
  deployment: 'Fornitura',
  freshness: 'Aggiornamento dati',
  inventory: 'Inventario (BOM)',
  mapping: 'Mapping',
  'prefect-run': 'Esecuzione job Prefect',
  'prefect-schedule': 'Schedulazione Prefect',
  product: 'Prodotto',
  'product-catalog': 'Catalogo prodotti',
  product_ownership: 'Responsabili prodotto',
  product_triage: 'Ticket',
  release: 'Release',
  release_component: 'Componente release',
  role_permissions: 'Permessi dei ruoli',
  roles: 'Ruoli',
  seed: 'Seed',
  settings: 'Impostazioni',
  sla_timer: 'Timer SLA (report ENISA)',
  srp_report: 'Report ENISA',
  sync: 'Sincronizzazione',
  triage_decision: 'Decisione di triage',
  ui_capabilities: 'Funzionalità UI',
  vex_decision: 'Decisione VEX',
};

/** Tipi entità noti (emessi dai servizi), ordinati. */
export const KNOWN_ENTITY_TYPES: readonly string[] = Object.keys(ENTITY_TYPE_LABEL).sort();

export const entityTypeLabel = (t: string): string => ENTITY_TYPE_LABEL[t] ?? t;

/** Discriminanti degli eventi del modulo ENISA (srp-service). */
export const ENISA_FORM_FIELD_KIND = 'enisa_form_field';
export const ENISA_FORM_EXPORT_KIND = 'enisa_form_export';

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/** Sottotipo leggibile dell'evento (es. campo del modulo ENISA), oltre all'azione. */
export function auditEventKindLabel(ev: Pick<AuditEventRow, 'after'>): string | null {
  const kind = asRecord(ev.after)['kind'];
  if (kind === ENISA_FORM_FIELD_KIND) return 'Modulo ENISA — campo modificato';
  if (kind === ENISA_FORM_EXPORT_KIND) return 'Modulo ENISA — export JSON';
  return null;
}

const valoreModulo = (side: Record<string, unknown>) =>
  formatAuditValue(side['valueLabel'] ?? side['value']);

/**
 * Righe del dettaglio di un evento. Per i campi del modulo ENISA una sola riga
 * «Etichetta ENISA (percorso): prima → dopo»; per gli altri il diff campo per campo.
 */
export function auditEventDetailRows(
  ev: Pick<AuditEventRow, 'before' | 'after'>,
): AuditChangeRow[] {
  const before = asRecord(ev.before);
  const after = asRecord(ev.after);
  if (after['kind'] === ENISA_FORM_FIELD_KIND) {
    const field = String(after['field']);
    const prima =
      before['value'] === null || before['value'] === undefined ? null : valoreModulo(before);
    return [
      {
        campo: field,
        label: `${String(after['label'] ?? field)} (${field})`,
        prima,
        dopo: valoreModulo(after),
        invariato: false,
      },
    ];
  }
  if (after['kind'] === ENISA_FORM_EXPORT_KIND) {
    return [
      {
        campo: 'revision',
        label: 'Revisione bozza esportata',
        prima: null,
        dopo: formatAuditValue(after['revision']),
        invariato: false,
      },
      {
        campo: 'complete',
        label: 'Completo',
        prima: null,
        dopo: after['complete']
          ? 'sì'
          : `no (${formatAuditValue(after['missingRequired'])} obbligatori mancanti)`,
        invariato: false,
      },
    ];
  }
  return auditChangeRows(ev);
}

/** Link al portale per l'entità dell'evento, se ha una pagina di dettaglio. */
export function auditEntityHref(ev: Pick<AuditEventRow, 'entityType' | 'entityId'>): string | null {
  if (ev.entityType === 'srp_report' && /^\d+$/.test(ev.entityId)) {
    return `/enisa-reports/${ev.entityId}`;
  }
  return null;
}

/** `datetime-local` (ora locale del browser) → ISO UTC; '' resta ''. */
export function localDateTimeToIso(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}
